import React, { useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styled from 'styled-components';
import { breakpoints, media } from 'utils/rwd';

const MAX_VISIBILITY = 2;

const getActiveRange = (count, activeCount) => {
  const leftRadius = Math.floor((activeCount - 1) / 2);
  const rightRadius = activeCount - 1 - leftRadius;

  return {
    leftRadius,
    rightRadius,
    minActive: leftRadius,
    maxActive: Math.max(leftRadius, count - 1 - rightRadius),
  };
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const Carousel3D = ({
  items = [],
  renderItem,
  maxVisibility = MAX_VISIBILITY,
  variant = 'looks',
}) => {
  const count = items.length;
  const isMulti = variant === 'products';
  const lgQuery = `(max-width: ${breakpoints.lg - 1}px)`;
  const getIsSmall = () => window.matchMedia(lgQuery).matches;
  const [isSmall, setIsSmall] = useState(getIsSmall);
  const [active, setActive] = useState(() => {
    const initialActiveCount = isMulti && !getIsSmall() ? Math.min(3, Math.max(1, count)) : 1;
    return getActiveRange(count, initialActiveCount).minActive;
  });
  const [pressedDir, setPressedDir] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(lgQuery);
    const handler = (e) => setIsSmall(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [lgQuery]);

  const activeCount = isMulti && !isSmall ? Math.min(3, Math.max(1, count)) : 1;
  const { leftRadius, rightRadius, minActive, maxActive } = getActiveRange(count, activeCount);
  const leftEdge = active - leftRadius;
  const rightEdge = active + rightRadius;

  useEffect(() => {
    setActive((i) => clamp(i, minActive, maxActive));
  }, [maxActive, minActive]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        clearTimeout(timerRef.current);
        setActive((i) => clamp(i - 1, minActive, maxActive));
        setPressedDir('left');
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        clearTimeout(timerRef.current);
        setActive((i) => clamp(i + 1, minActive, maxActive));
        setPressedDir('right');
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        timerRef.current = setTimeout(() => setPressedDir(null), 150);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      clearTimeout(timerRef.current);
    };
  }, [maxActive, minActive]);

  return (
    <CarouselWrapper>
      {active > minActive && (
        <NavButton
          className="left"
          $pressed={pressedDir === 'left'}
          onClick={() => setActive((i) => clamp(i - 1, minActive, maxActive))}
        >
          <FaChevronLeft style={{ transform: 'translateX(-2px)' }} />
        </NavButton>
      )}
      {items.map((item, i) => {
        const isActive = i >= leftEdge && i <= rightEdge;
        const windowPos = i - active;

        let offset3d = 0;
        if (i < leftEdge) {
          offset3d = i - leftEdge;
        } else if (i > rightEdge) {
          offset3d = i - rightEdge;
        }

        const absOffset = Math.abs(offset3d);
        const direction = -Math.sign(offset3d);

        const Container = isMulti ? CardContainerHorizontal : CardContainer3D;
        const isVisible = absOffset <= maxVisibility;

        if (absOffset > maxVisibility + 1) {
          return null;
        }

        return (
          <Container
            // eslint-disable-next-line react/no-array-index-key
            key={item.uid || i}
            style={{
              '--window-pos': windowPos,
              '--active-count': activeCount,
              '--offset-3d': offset3d,
              '--abs-offset': absOffset,
              '--direction': direction,
              pointerEvents: isActive ? 'auto' : 'none',
              opacity: isVisible ? 1 : 0,
              zIndex: isMulti ? 10 - absOffset : undefined,
            }}
          >
            {renderItem(item, isActive, i)}
          </Container>
        );
      })}
      {active < maxActive && (
        <NavButton
          className="right"
          $pressed={pressedDir === 'right'}
          onClick={() => setActive((i) => clamp(i + 1, minActive, maxActive))}
        >
          <FaChevronRight style={{ transform: 'translateX(2px)' }} />
        </NavButton>
      )}
    </CarouselWrapper>
  );
};

export default Carousel3D;

const CarouselWrapper = styled.div`
  margin: 0 auto;
  position: relative;
  outline: none;
  width: 100%;
  height: 100%;
  max-width: 1000px;
  perspective: 400px;
  transform-style: preserve-3d;
  ${media.xs`
    width: 80%;
    height: 80%;
  `}
`;

// Original 3D carousel — Looks page (activeCount=1)
// offset3d/3 reconstructs the original (active-i)/3 offset; division kept in CSS
// so that --abs-offset stays as a plain integer distance from window edge.
const CardContainer3D = styled.div`
  position: absolute;
  inset: 0;
  transform: rotateY(calc(var(--offset-3d) / 3 * -50deg))
    scaleY(calc(1 + var(--abs-offset) / 3 * -0.4)) translateZ(calc(var(--abs-offset) / 3 * -580px))
    translateX(calc(var(--direction) * -0px));
  filter: blur(calc(var(--abs-offset) / 3 * 3px));
  backface-visibility: hidden;
  transform-style: preserve-3d;
  will-change: transform, opacity, filter;
  transition:
    transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 240ms ease,
    filter 240ms ease;

  ${media.xs`
    transform: rotateY(calc(var(--offset-3d) / 3 * -50deg))
      scaleY(calc(1 + var(--abs-offset) / 3 * -0.4))
      translateZ(calc(var(--abs-offset) / 3 * -580px))
      translateX(calc(var(--direction) * -75px));
  `}
  ${media.md`
    transform: rotateY(calc(var(--offset-3d) / 3 * -50deg))
      scaleY(calc(1 + var(--abs-offset) / 3 * -0.4))
      translateZ(calc(var(--abs-offset) / 3 * -580px))
      translateX(calc(var(--direction) * -100px));
  `}

  @media (prefers-reduced-motion: reduce) {
    transform: translateX(calc(var(--offset-3d) * -12%)) scale(calc(1 - var(--abs-offset) * 0.04));
    filter: none;
    transition: opacity 1ms linear;
  }
`;

// Horizontal strip with mild 3D on peeking items — Products page (activeCount>1)
// translateX(windowPos * 100%) slides the strip; 100% = card slot width (1/N of container).
// 3D effects only kick in for items outside the active window (offset3d ≠ 0).
const CardContainerHorizontal = styled.div`
  padding-top: 5px;
  position: absolute;
  top: 0;
  bottom: 0;
  width: calc(100% / var(--active-count));
  left: 50%;
  margin-left: calc(-50% / var(--active-count));
  transform: translateX(calc(var(--window-pos) * 100%)) rotateY(calc(var(--offset-3d) * -45deg))
    translateZ(calc(var(--abs-offset) * -150px)) scale(calc(1 - var(--abs-offset) * 0.1));
  filter: blur(calc(var(--abs-offset) * 2px));
  backface-visibility: hidden;
  will-change: transform, opacity, filter;
  transition:
    transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 240ms ease,
    filter 240ms ease;
  transform-style: preserve-3d;

  @media (prefers-reduced-motion: reduce) {
    transform: translateX(calc(var(--window-pos) * 100%));
    filter: none;
    transition: opacity 1ms linear;
  }
`;

const NavButton = styled.button`
  position: absolute;
  bottom: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background-color: #be1622;
  color: white;
  font-size: 32px;
  z-index: 15;
  cursor: pointer;
  user-select: none;
  border: none;
  border-radius: 50%;
  opacity: ${({ $pressed }) => ($pressed ? 1 : 0.5)};
  transition: opacity 0.2s ease;
  &.left {
    left: -16px;
    transform: translate(-100%, 50%);
  }
  &.right {
    right: -16px;
    transform: translate(100%, 50%);
  }
  &.left,
  &.right {
    &:hover {
      opacity: 1;
    }
    &:active {
      opacity: 1;
    }
  }
  ${media.xs.down`
    width: 40px;
    height: 40px;
    font-size: 24px;
    &.left {
      left: 8px;
      transform: translateY(50%);
    }
    &.right {
      right: 8px;
      transform: translateY(50%);
    }
  `}
  ${media.md.down`
    &.left {
      left: 0px;
      transform: translateY(50%);
    }
    &.right {
      right: 0px;
      transform: translateY(50%);
    }
  `}
`;
